import React from "react";
import { TableRow, TableCell } from "@mui/material";

/**
 * CustomTableRow
 *
 * Fixes the sticky-column hover transparency bug:
 * Instead of relying on the CSS cascade (which applies semi-transparent
 * MUI hover colour on top of sticky backgroundColor → bleed-through),
 * we track hover state in JS and swap to a pre-computed solid colour.
 *
 * Props from CustomTable:
 *  row              – single data object
 *  columns          – column defs array
 *  isLast           – whether this is the last row
 *  bodyStickyBg     – default opaque bg for sticky cells
 *  hoverSolid       – opaque bg for normal cells on hover
 *  hoverStickySolid – opaque bg for sticky cells on hover (no alpha bleed)
 *  borderClr        – divider colour
 */
const CustomTableRow = ({
    row,
    columns,
    isLast,
    bodyStickyBg,
    hoverSolid,
    hoverStickySolid,
    borderClr,
}) => {

    return (
        <TableRow
            sx={{
                "& .MuiTableCell-root": {
                    py: "10px",
                    px: "12px",
                },

                "&:hover .non-sticky-cell": {
                    backgroundColor: hoverSolid,
                },

                "&:hover .sticky-cell": {
                    backgroundColor: hoverStickySolid,
                },
            }}
        >
            {columns.map((col, idx) => {
                const isStickyFirst = !!col.stickyFirst;
                const isStickyLast = !!col.stickyLast;
                const isSticky = isStickyFirst || isStickyLast;
                const align = col.align || "center";

                const cellValue = col.render
                    ? col.render(row[col.field], row)
                    : (row[col.field] ?? "—");

                // Sticky cells get their own solid hover bg so content behind
                // never bleeds through the semi-transparent overlay.
                const stickyActiveBg = bodyStickyBg;
                return (
                    <TableCell
                        key={idx}
                        align={align}
                        className={isSticky ? "sticky-cell" : "non-sticky-cell"}

                        sx={{
                            whiteSpace: col.wrap ? "normal" : "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: col.maxWidth ?? "unset",
                            transition: "background-color 0.15s ease",
                            height: 60,
minHeight: 60,

                            /* last row — remove bottom border (container edge handles it) */
                            ...(isLast && { borderBottom: "none" }),

                            /* ── sticky-first (plan_name) ── */
                            ...(isStickyFirst && {
                                position: "sticky",
                                left: 0,
                                zIndex: 3,
                                backgroundColor: stickyActiveBg,   // always opaque
                                borderRight: `2px solid ${borderClr}`,
                                  pl: 2,

                            }),

                            /* ── sticky-last (action) ── */
                            ...(isStickyLast && {
                                position: "sticky",
                                right: 0,
                                zIndex: 3,
                                backgroundColor: stickyActiveBg,   // always opaque
                                borderLeft: `2px solid ${borderClr}`,
                                borderRight: "none",
                            }),

                            /* non-sticky — keep transparent so row bg shows */
                            ...(!isSticky && {
                                backgroundColor: "transparent",
                            }),

                            ...col.sx,
                        }}
                    >
                        {cellValue}
                    </TableCell>
                );
            })}
        </TableRow>
    );
};

export default CustomTableRow;